import { ur } from "@faker-js/faker";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Card, Col, Form, Input, Layout, Modal, notification, Radio, Row, Select, Space, Table, TableProps, Tag, Typography } from "antd";
import React, { useEffect, useState, useRef } from 'react';
import AdminApiService from "src/api/AdminApiService";
import { ApiError } from "src/api/ApiResultHandler";
import CustomTag from "src/components/CustomTag";
import AutomationType from "src/consts/AutomationType";
import IotModel from "src/consts/IotModel";
import AddAzureDeviceDTO from "src/models/AddAzureDeviceDTO";
import AzureIoTEdgeDeviceDTO from "src/models/AzureIoTEdgeDeviceDTO";
import { useGetAllAzureIoTEdgeDevicesQuery } from "src/queries/useGetAllAzureIoTEdgeDevicesQuery";
import { getEnumDictionary, getEnumKeys } from "src/utils/enumUtils";

const { Title, Text } = Typography;

const IoTDevices = () => {
    const [form] = Form.useForm<AddAzureDeviceDTO>();
    const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
    const [submitButtonEnabled, setSubmitButtonEnabled] = useState(false);
    const [showDeviceDetailModal, setShowDeviceDetailModal] = useState(false);
    const [selectDevice, setSelectedDevice] = useState<AzureIoTEdgeDeviceDTO>();
    const queryClient = useQueryClient();
    
    const { getAllAzureIoTEdgeDevicesQueryKey, getAllAzureIoTEdgeDevicesQuery } = useGetAllAzureIoTEdgeDevicesQuery({ enabled: true, refetchOnWindowFocus: false });

    const handleAddDeviceButtonClicked = () => {
        setShowAddDeviceModal(true);
    }

    const handleCloseAddDeviceModal = () => {
        setShowAddDeviceModal(false);
    }

    const handleOpenDeviceDetailModal = (deviceId: string) => {
        const device = getAllAzureIoTEdgeDevicesQuery.data?.find(d => d.deviceId == deviceId);
        if(device != null){
            setSelectedDevice(device);
            setShowDeviceDetailModal(true);
        }else{
            notification.error({ message: 'We could not find the device specified for some reason. Contact support' });
        }

    }

    const handleCloseDeviceDetailmodal = () => {
        setShowDeviceDetailModal(false);
    }

    const handleDownloadProvisionsClicked = async () => {
        try {

            if (selectDevice?.deviceId != null) {
                const url = `${window.location.protocol}//${window.location.host}/api/admin/getAzureIotEdgeDeviceProvisions`;
                const requestURL = new URL(url);
                requestURL.searchParams.set('deviceId', selectDevice.deviceId);
    
                // Fetch the zip file from the API
                const response = await fetch(requestURL.toString(), {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/zip',
                    },
                });
    
                if (!response.ok) {
                    throw new Error(`Failed to download: ${response.status} ${response.statusText}`);
                }

                // Convert response to Blob
                const blob = await response.blob();
    
                // Create a download link and trigger click
                const downloadUrl = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = downloadUrl;
                a.download = `provisions_${selectDevice.deviceId}.zip`; // Set the download file name
                document.body.appendChild(a);
                a.click();
    
                // Cleanup
                URL.revokeObjectURL(downloadUrl);
                document.body.removeChild(a);
            }
        } catch (err) {
            console.error("Error downloading provisions:", err);
        }
    };
    
    
    
    

    const columns: TableProps<AzureIoTEdgeDeviceDTO>['columns'] = [
        {
            title: 'Name',
            key: 'deviceId',
            dataIndex: 'deviceId',
            render: (text) => <a onClick={ ()=>{ handleOpenDeviceDetailModal(text) }}>{text}</a>,
        },
        {
            title: 'Tags',
            key: 'tags',
            dataIndex: 'tags',
            render: (tags) => tags.map( (tag: string) => <CustomTag label={tag}/>),
        }
    ];

    const iotModelOptions =[    
        { value: -1, label: 'Select an IoT Model' },
        ...(getEnumDictionary(IotModel) ?? []).map( (g) => (
            { value: g.value, label: g.label }
        ))
    ]

    useEffect( () => {

        if(getAllAzureIoTEdgeDevicesQuery.isError){
            notification.error({
                message: "Could not submit IoT device",
                description: getAllAzureIoTEdgeDevicesQuery.error ?? "Contact support"
            })
        }

    }, [getAllAzureIoTEdgeDevicesQuery.dataUpdatedAt])
    const onAddNewDeviceFinish = async (values: AddAzureDeviceDTO) => {

        try{
            const result = await AdminApiService.addAzureIotEdgeDevice(values);
            notification.success({ message: 'IoT Device successfully submitted' });
            queryClient.invalidateQueries({queryKey:[getAllAzureIoTEdgeDevicesQueryKey]});
            handleCloseAddDeviceModal();
        }catch(err){
            notification.error({
                message: "Could not submit IoT device",
                description: err?.errorDetails?.detail ?? "Contact support"
            });
        }
    }

    return(
        <Layout.Content>
            <Row gutter={16}>
                <Col xs={24} lg={6}>
                    <Card>
                        <Button
                        onClick={handleAddDeviceButtonClicked}
                        >
                            Add a new device
                        </Button>
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Table<AzureIoTEdgeDeviceDTO> 
                    scroll={{ y: "60vh"}}
                    columns={columns} 
                    dataSource={getAllAzureIoTEdgeDevicesQuery.data ?? []} 
                    loading={getAllAzureIoTEdgeDevicesQuery.isFetching}
                    pagination={false}
                    />
                </Col>
            </Row>

            <Modal
            open={showDeviceDetailModal}
            onClose={handleCloseDeviceDetailmodal}
            onCancel={handleCloseDeviceDetailmodal}
            okButtonProps={{ style: { display:'none' } }}
            cancelButtonProps={{ style: { display:'none' } }}
            destroyOnClose
            >
                
                <Title level={2}>{selectDevice?.deviceId}</Title>
                <Row>
                    <Button
                    onClick={handleDownloadProvisionsClicked}
                    >Download Provisions</Button>
                </Row>
            </Modal>

            <Modal
            open={showAddDeviceModal}
            onClose={handleCloseAddDeviceModal}
            onCancel={handleCloseAddDeviceModal}
            okButtonProps={{ style: { display:'none' } }}
            cancelButtonProps={{ style: { display:'none' } }}
            destroyOnClose
            >
                <Form
                form={form}
                onFinish={onAddNewDeviceFinish}>
                    <Title level={3}>Add a new device</Title>

                    <Row>
                        <Form.Item
                        name={'iotModelType'}
                        label={'Pick your IoT Model type'}
                        rules={[ 
                            {
                                validator: (_, value) => {
                                    if(value === -1){
                                        setSubmitButtonEnabled(false);
                                    }else if(value === 0 || value === 1){
                                        setSubmitButtonEnabled(true);
                                    }
                                }
                            }
                        ]}
                        >
                            <Select 
                            options={iotModelOptions} 
                            style={{
                                width: '200px',
                            }}/>
                        </Form.Item>
                    </Row>
                    <br/>
                    <Row>
                        <Form.Item
                        name={'serialNumber'}
                        label={'Enter the device\'s serial number'}
                        rules={[
                            { required: true },
                            { message: 'Please enter the device\'s serial number'}
                        ]}
                        >
                            <Input placeholder="i.e RG1238701..."/>
                        </Form.Item>
                    </Row>
                    <br/>
                    <Row justify={'center'}>
                        <Form.Item
                        name={'automationType'}
                        label={'Automation Type'}
                        rules={[
                            { required: true }
                        ]}>
                            <Radio.Group>
                                <Radio value={AutomationType.DriStack}>DriStack</Radio>
                                <Radio value={AutomationType.AutoBin}>AutoBin</Radio>
                                <Radio value={AutomationType.PremierPlus}>Premier+</Radio>
                            </Radio.Group>
                        </Form.Item>
                    </Row>
                    <br/>
                    <Row justify={'center'}>
                        <Form.Item>
                            <Button onClick={() => onAddNewDeviceFinish(form.getFieldsValue())}disabled={!submitButtonEnabled} type='primary' htmlType="submit">Submit</Button>
                        </Form.Item>
                    </Row>

                </Form>
            </Modal>
        </Layout.Content>
    )
}

export default IoTDevices;

